/*
 * ============================================================
 * SAV_ERP — Smart Account Vision Enterprise Resource Planning
 * Copyright (c) 2026 Hatem Mohamed Abdalla.
 * All rights reserved.
 *
 * This file is part of a proprietary software product owned
 * solely by Hatem Mohamed Abdalla (UAE, +971 50 390 9781).
 * Copying, redistribution, modification, reverse engineering
 * or reuse in any form without prior written consent of the
 * copyright owner is strictly prohibited and will be
 * prosecuted under UAE Federal Law No. 38/2021 on Copyright
 * and applicable international treaties.
 *
 * Fingerprint: SAV-ERP-HMA-2026-#a7f3d0
 * ============================================================
 */
const DEFAULT_BACKEND_PORT = '8001';
const getRuntimeBackendPort = () => {
  if (typeof window === 'undefined') return DEFAULT_BACKEND_PORT;
  try {
    return window.SAV_ERP_BACKEND_PORT || window.localStorage.getItem('sav_erp_backend_port') || DEFAULT_BACKEND_PORT;
  } catch (_error) {
    return DEFAULT_BACKEND_PORT;
  }
};
const DEFAULT_BACKEND_URL = `http://127.0.0.1:${DEFAULT_BACKEND_PORT}`;

const inferLanBackendUrl = () => {
  if (typeof window === 'undefined') return DEFAULT_BACKEND_URL;
  const protocol = window.location.protocol || 'http:';
  const hostname = window.location.hostname || '127.0.0.1';
  const host = window.location.host || `${hostname}:${DEFAULT_BACKEND_PORT}`;
  const currentPort = window.location.port || '';
  const backendPort = getRuntimeBackendPort();
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  if (currentPort && currentPort !== '3000') return `${protocol}//${host}`;
  if (isLocal) return `${protocol}//127.0.0.1:${backendPort}`;
  return `${protocol}//${hostname}:${backendPort}`;
};

export const sanitizeBackendUrl = (rawValue, fallback = inferLanBackendUrl()) => {
  const value = String(rawValue ?? '').trim();
  const stripped = value
    .replace(/^['"`]+|['"`]+$/g, '')
    .replace(/\s+/g, '')
    .replace(/\/+$/g, '');

  if (!stripped) return fallback;

  try {
    const normalized = new URL(stripped).toString().replace(/\/+$/g, '');
    return normalized;
  } catch (_error) {
    return fallback;
  }
};

export const getBackendUrl = (fallback = inferLanBackendUrl()) => {
  const raw = String(process.env.REACT_APP_BACKEND_URL ?? '').trim();
  const resolved = (!raw || raw.toLowerCase() === 'auto')
    ? fallback
    : sanitizeBackendUrl(raw, fallback);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem('sav_erp_backend_url', resolved);
    } catch (_error) {
      // ignore storage failures
    }
  }
  return resolved;
};

export const buildBackendUrl = (path = '', fallback = inferLanBackendUrl()) => {
  const base = getBackendUrl(fallback);
  if (!path) return base;
  if (/^https?:\/\//i.test(path)) return path;
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

export default getBackendUrl;
